/**
 * LmnProductTile — standalone product tile for LMN (Letter of Medical Necessity)
 * products. Cloned from the standard tile (clone-over-flags, per the PDP-variant
 * convention) rather than branched inline, so the LMN treatment is a self-contained
 * unit: "LMN Required" badge, favorites heart, image, rating, name, "What's an LMN?"
 * modal link, and a "Get Started" CTA → PDP. NO price, NO add-to-cart, NO quantity
 * selector, NO swatches, NO quick view (SFCC-11677).
 *
 * Dispatched by `HecProductTileCms` when `isLMNProduct(product)` is true, so it
 * applies across every tile surface (PLP, search, carousels, recs, favorites).
 *
 * Source references:
 * - sfcc-website/org_hec/cartridge/templates/default/product/productTile.isml (LMN branch)
 * - hec-composable/hec/components/product-tile-cms/productTileCms.tsx
 */
import { Link, type LinkProps } from "react-router";

import { ProductImage } from "~/components/product-list/product-image";
import { TriButton } from "~/components/ui/tri-button";
import { useSite, useURL } from "~/contexts";
import type { HecProduct } from "~/hec/types/product";
import { getProductBadges } from "~/hec/types/product";
import { buildProductUrl } from "~/hec/utils/product-slug";
import { cn } from "~/lib/utils";
import { useLayoutData } from "~/routes/layout";
import { FavoritesCTA } from "~/routes/resources/favorites";

import { LmnTileContent } from "./lmn-tile-content";
import { ProductBadges } from "./product-badges";
import { Ratings } from "./ratings";

interface LmnProductTileProps {
  product: HecProduct;
  /** Show ratings (default true; set false for basic/compact view). */
  enableRatings?: boolean;
  /** Star-color variant — "recs" on recommendation carousels, else "default". */
  ratingsVariant?: "default" | "recs";
  /** Show favorites heart CTA (default true; recs carousels set false). */
  enableFavorites?: boolean;
  /** Favorites page mode: hides the add-heart, shows a Remove button so a saved
   *  LMN product can still be removed from its tile. */
  favoritesMode?: boolean;
  /** Site ID override — defaults to SiteContext site.id. */
  siteId?: string;
  className?: string;
  style?: React.CSSProperties;
  /** Route prefetch strategy for the product link. */
  prefetch?: LinkProps["prefetch"];
  /** COM-158: above-the-fold tiles (first PLP row) render their image eager +
   *  fetchPriority="high" so they become LCP candidates. Default lazy. */
  imagePriority?: boolean;
}

export function LmnProductTile({
  product,
  enableRatings = true,
  ratingsVariant = "default",
  enableFavorites = true,
  favoritesMode = false,
  siteId: siteIdProp,
  className,
  style,
  prefetch = "intent",
  imagePriority = false,
}: LmnProductTileProps) {
  const url = useURL();
  const { site } = useSite();
  const { lmnContent } = useLayoutData();
  const siteId = siteIdProp ?? site.id;

  const badges = getProductBadges(product);
  const productName = product.name || product.productName || "";
  const productUrl = product.url || buildProductUrl(product.id, productName);

  // `add_to_wishlist` payload (SFCC-12175 W6). No `price`: an LMN product has
  // no price/ATC/stock concept (see the image-container comment below), so GA4
  // correctly receives an item with no price and the event omits `value`.
  const lmnWishlistGa4 = {
    id: product.id,
    name: productName,
    brand: product.brand,
    category: product.categoryPath ?? "",
  };
  const imageUrl = product.image_url || product.image?.link;
  const tileImage = product.image
    ? { link: product.image.link, alt: product.image.alt ?? productName }
    : imageUrl
      ? { link: imageUrl, alt: productName }
      : undefined;

  return (
    <div
      className={cn("overflow-hidden rounded-md bg-white", className)}
      style={style}
      data-testid={`product-tile-root-${product.id}`}
    >
      {/* Image container with badge + favorites heart. No quick view, and no OOS
          overlay by design — an LMN product is not purchasable here (no price /
          ATC / stock concept), matching SFRA's LMN tile branch. */}
      <div className="aspect-square relative w-full">
        {badges.length > 0 && (
          <ProductBadges
            badges={badges}
            productId={product.id}
            lmnBadgeColor={lmnContent.badgeColor}
          />
        )}

        {enableFavorites && (
          <FavoritesCTA
            productId={product.id}
            className="absolute right-2 top-2 z-10"
            iconHeight={22}
            iconWidth={22}
            noBorder
            wishlistGa4={lmnWishlistGa4}
          />
        )}

        <Link
          to={url(productUrl)}
          prefetch={prefetch}
          state={{ tileProduct: product }}
          className="block"
          data-testid={`product-tile-image-${product.id}`}
        >
          {tileImage ? (
            <ProductImage
              image={tileImage}
              type="plp"
              priority={imagePriority}
            />
          ) : (
            <div className="aspect-square flex items-center justify-center rounded text-sm text-gray-400">
              No image
            </div>
          )}
        </Link>
      </div>

      {/* Tile body */}
      <div className="px-2 pb-2 pt-2 text-left">
        {enableRatings && (
          <div className="mt-2 flex">
            <Ratings
              rating={product.rating ?? 0}
              reviewCount={product.reviewCount}
              siteId={siteId}
              size="small"
              variant={ratingsVariant}
            />
          </div>
        )}

        <div className="mt-2 min-h-[3rem]">
          <Link
            to={url(productUrl)}
            prefetch={prefetch}
            state={{ tileProduct: product }}
            className="mb-[.5rem] line-clamp-2 font-grotesque font-medium leading-6 text-blue-04"
            data-testid={`product-tile-name-${product.id}`}
          >
            {productName}
          </Link>
        </div>

        <LmnTileContent productId={product.id} productUrl={productUrl} />

        {/* Remove button — favorites page only. Keeps a saved LMN product
            removable from its tile (the add-heart is hidden in favorites mode). */}
        {favoritesMode && (
          <FavoritesCTA
            productId={product.id}
            className="mt-2 flex"
            isFavoritesPage={true}
            wishlistGa4={lmnWishlistGa4}
          >
            <TriButton variant="link" className="self-end hover:no-underline">
              <span className="text-sm font-light text-remove hover:underline">
                Remove
              </span>
            </TriButton>
          </FavoritesCTA>
        )}
      </div>
    </div>
  );
}
